import { Arrow } from "../components/Brand";
import { PageMeta } from "../components/PageMeta";
import { ProjectMedia } from "../components/ProjectMedia";
import {
  CTASection,
  PageHero,
  SectionHeading,
  TagList,
} from "../components/Sections";
import { products, selfDevelopedProjects } from "../data/site";
import { useI18n, useLocalizedContent } from "../i18n";

export function ProductsPage() {
  const { l, locale } = useI18n();
  const localizedProducts = useLocalizedContent(products);
  const vibehub = useLocalizedContent(selfDevelopedProjects)[0];

  return (
    <>
      <PageMeta title={l("产品", "Products")} />
      <PageHero
        eyebrow={l("产品", "PRODUCTS")}
        title={
          <>
            {l("把自研能力，做成", "Turning our own engineering into ")}
            <span className="blue-text">
              {l("真正的产品。", "real products.")}
            </span>
          </>
        }
        lead={l(
          "VibeHub 是 AccSci 自主研发并持续运营的 AI 原生产品。与此同时，我们把 Agent、Edge AI 与智能硬件经验持续沉淀为可复用的产品能力。",
          "VibeHub is an AI-native product designed, built, and operated by AccSci. We also turn our agent, edge AI, and smart-hardware experience into reusable product foundations.",
        )}
      />
      {vibehub ? (
        <section className="section product-feature">
          <div className="container product-feature__grid">
            <div className="product-feature__visual">
              <ProjectMedia project={vibehub} />
            </div>
            <div className="product-feature__body">
              <span className="product-status">
                <i /> {l("自研产品 · 在线运行", "IN-HOUSE PRODUCT · LIVE")}
              </span>
              <h2>{vibehub.title}</h2>
              <p>{vibehub.summary}</p>
              <TagList tags={vibehub.tags} />
              <dl className="product-feature__facts">
                <div>
                  <dt>{l("产品形态", "Product")}</dt>
                  <dd>{vibehub.projectType}</dd>
                </div>
                <div>
                  <dt>{l("研发范围", "Scope")}</dt>
                  <dd>{vibehub.delivery}</dd>
                </div>
              </dl>
              {vibehub.liveUrl ? (
                <a
                  className="button button--dark"
                  href={vibehub.liveUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {l("访问 VibeHub", "Visit VibeHub")}{" "}
                  <Arrow direction="up-right" />
                </a>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
      <section className="products-list section">
        <div className="container">
          <SectionHeading
            eyebrow={l("产品方向", "PRODUCT DIRECTIONS")}
            title={l(
              "从项目经验中，持续沉淀产品底座。",
              "Product foundations shaped by real project work.",
            )}
            description={l(
              "以下为 AccSci 正在验证和演进的产品方向。",
              "These are product directions AccSci is actively validating and evolving.",
            )}
          />
          <div className="product-direction-list">
            {localizedProducts.map((product, index) => (
              <article className="product-row" key={product.id}>
                <div className="product-row__index">
                  D—{String(index + 1).padStart(2, "0")}
                </div>
                <div className="product-row__visual">
                  <div className="blueprint-corners">
                    <i />
                    <i />
                    <i />
                    <i />
                  </div>
                  <img
                    src={product.image.src}
                    alt={product.image.alt}
                    width={product.image.width}
                    height={product.image.height}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{product.id.toUpperCase()}</span>
                </div>
                <div className="product-row__body">
                  <span className="product-status">
                    <i />
                    {product.status}
                  </span>
                  <h2>{product.name}</h2>
                  <h3>{product.tagline}</h3>
                  <p>{product.description}</p>
                  <ul>
                    {product.features.map((feature) => (
                      <li key={feature}>
                        <Arrow />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section product-logic dark-section">
        <div className="container">
          <SectionHeading
            inverse
            eyebrow={l("产品逻辑", "PRODUCT LOGIC")}
            title={l(
              "来自一线问题，回到一线系统。",
              "Born from field problems. Proven in field systems.",
            )}
          />
          <div className="logic-line">
            {(locale === "zh"
              ? [
                  ["01", "在真实项目中发现重复问题"],
                  ["02", "把工程经验抽象为稳定模式"],
                  ["03", "沉淀为可复用的产品能力"],
                  ["04", "回到现场持续验证和演进"],
                ]
              : [
                  ["01", "Find a recurring problem in real projects"],
                  [
                    "02",
                    "Abstract engineering experience into a stable pattern",
                  ],
                  ["03", "Build it into a reusable product capability"],
                  ["04", "Return to the field to validate and evolve it"],
                ]
            ).map(([index, text]) => (
              <div key={index}>
                <span>{index}</span>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  );
}
